import { supabase } from '@/lib/supabase/client'

export type Locale = 'pt' | 'en'

export type CtaConfig = {
  label: string
  url: string
}

export type SiteSettings = {
  id: string
  hero_title: string
  hero_description: string
  hero_cta_text: string
  hero_image_url?: string | null
  dashboard_image_url?: string | null
  explainer_video_url?: string | null
  explainer_video_title?: string | null
  contact_email: string
  footer_text: string
  company_logo_url?: string | null
  address?: string | null
  phone_number?: string | null
  linkedin_url?: string | null
  instagram_url?: string | null
  facebook_url?: string | null
  chatbot_primary_color?: string | null
  chatbot_secondary_color?: string | null
  chatbot_cta_config?: CtaConfig[] | null
  locale: string
  updated_at?: string
}

export type SiteSettingsInsert = Omit<SiteSettings, 'id' | 'updated_at'>

export type FormConfig = {
  show_employee_count: boolean
  show_uses_excel: boolean
  custom_challenge_label?: string
}

export type PracticalExample = {
  title: string
  description: string
}

export type PageContent = {
  hero: { title: string; cta: string }
  problems: string[]
  saas_examples: { name: string; description: string; benefit: string }[]
  practical_examples?: PracticalExample[]
  ai_capabilities: string[]
  business_impact: { label: string; value: string }[]
  seo: { description: string; keywords: string; title?: string }
  form_config?: FormConfig
}

export type AiSummary = {
  title: string
  content: string
  highlights: string[]
}

export type FaqItem = {
  question: string
  answer: string
}

export type BusinessSector = {
  id: string
  title: string
  description: string
  icon_name: string
  image_url?: string | null
  sort_order: number
  locale: string
  slug?: string | null
  page_content?: PageContent | null
  ai_summary?: AiSummary | null
  faq_structured_data?: FaqItem[] | null
}

export type BusinessSectorInsert = Omit<BusinessSector, 'id'>

export type Faq = {
  id: string
  question: string
  answer: string
  locale: string
  sort_order: number
  created_at: string
}

export type FaqInsert = Omit<Faq, 'id' | 'created_at'>

export type Testimonial = {
  id: string
  author_name: string
  author_role: string | null
  content: string
  author_avatar_url: string | null
  rating: number
  locale: string
  sort_order: number
  created_at: string
  sector_id?: string | null
}

export type TestimonialInsert = Omit<Testimonial, 'id' | 'created_at'>

export type Partner = {
  id: string
  name: string
  logo_url: string
  website_url?: string | null
  sort_order: number
  created_at: string
}

export type PartnerInsert = Omit<Partner, 'id' | 'created_at'>

export type CaseStudy = {
  id: string
  title: string
  client_name: string | null
  results_description: string | null
  image_url: string | null
  sector_id: string | null
  sort_order: number | null
  created_at: string | null
}

// Helper to get settings with fallback logic
export const getSiteSettings = async (
  locale: string = 'pt',
  useFallback = true,
) => {
  // First try to get the requested locale
  let { data, error } = await supabase
    .from('site_settings' as any)
    .select('*')
    .eq('locale', locale)
    .single()

  // If not found and fallback is enabled and we are not already asking for PT
  if ((error || !data) && useFallback && locale !== 'pt') {
    return await supabase
      .from('site_settings' as any)
      .select('*')
      .eq('locale', 'pt')
      .single()
  }

  return { data: data as SiteSettings | null, error }
}

export const createSiteSettings = async (settings: SiteSettingsInsert) => {
  const { data, error } = await supabase
    .from('site_settings' as any)
    .insert([settings])
    .select()
    .single()

  return { data: data as SiteSettings, error }
}

export const updateSiteSettings = async (
  settings: Partial<SiteSettings> & { id: string },
) => {
  const { data, error } = await supabase
    .from('site_settings' as any)
    .update(settings)
    .eq('id', settings.id)
    .select()
    .single()

  return { data: data as SiteSettings, error }
}

// Helper to get sectors with fallback logic
export const getBusinessSectors = async (
  locale: string = 'pt',
  useFallback = true,
) => {
  const { data, error } = await supabase
    .from('business_sectors' as any)
    .select('*')
    .eq('locale', locale)
    .order('sort_order', { ascending: true })

  // If empty list and fallback is enabled and we are not already asking for PT
  if ((!data || data.length === 0) && useFallback && locale !== 'pt') {
    return await supabase
      .from('business_sectors' as any)
      .select('*')
      .eq('locale', 'pt')
      .order('sort_order', { ascending: true })
  }

  return { data: data as BusinessSector[], error }
}

export const getSectorBySlug = async (slug: string) => {
  const { data, error } = await supabase
    .from('business_sectors' as any)
    .select('*')
    .eq('slug', slug)
    .single()

  return { data: data as BusinessSector | null, error }
}

export const createBusinessSector = async (sector: BusinessSectorInsert) => {
  const { data, error } = await supabase
    .from('business_sectors' as any)
    .insert([sector])
    .select()
    .single()

  return { data: data as BusinessSector, error }
}

export const updateBusinessSector = async (
  id: string,
  sector: Partial<BusinessSectorInsert>,
) => {
  const { data, error } = await supabase
    .from('business_sectors' as any)
    .update(sector)
    .eq('id', id)
    .select()
    .single()

  return { data: data as BusinessSector, error }
}

export const deleteBusinessSector = async (id: string) => {
  const { error } = await supabase
    .from('business_sectors' as any)
    .delete()
    .eq('id', id)

  return { error }
}

// FAQ Functions

export const getFaqs = async (locale: string = 'pt', useFallback = true) => {
  const { data, error } = await supabase
    .from('faqs' as any)
    .select('*')
    .eq('locale', locale)
    .order('sort_order', { ascending: true })

  // If empty list and fallback is enabled and we are not already asking for PT
  if ((!data || data.length === 0) && useFallback && locale !== 'pt') {
    return await supabase
      .from('faqs' as any)
      .select('*')
      .eq('locale', 'pt')
      .order('sort_order', { ascending: true })
  }

  return { data: data as Faq[], error }
}

export const createFaq = async (faq: FaqInsert) => {
  const { data, error } = await supabase
    .from('faqs' as any)
    .insert([faq])
    .select()
    .single()

  return { data: data as Faq, error }
}

export const updateFaq = async (id: string, faq: Partial<FaqInsert>) => {
  const { data, error } = await supabase
    .from('faqs' as any)
    .update(faq)
    .eq('id', id)
    .select()
    .single()

  return { data: data as Faq, error }
}

export const deleteFaq = async (id: string) => {
  const { error } = await supabase
    .from('faqs' as any)
    .delete()
    .eq('id', id)

  return { error }
}

// Testimonial Functions

export const getTestimonials = async (
  locale: string = 'pt',
  sectorId?: string,
  useFallback = true,
) => {
  let query = supabase
    .from('testimonials' as any)
    .select('*')
    .eq('locale', locale)
    .order('sort_order', { ascending: true })

  if (sectorId) {
    query = query.eq('sector_id', sectorId)
  }

  const { data, error } = await query

  // Fallback logic
  if ((!data || data.length === 0) && useFallback) {
    // If we searched by sector and found nothing, try searching without sector (general ones)
    if (sectorId) {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('testimonials' as any)
        .select('*')
        .eq('locale', locale)
        .is('sector_id', null) // General testimonials have null sector_id
        .order('sort_order', { ascending: true })

      if (fallbackData && fallbackData.length > 0) {
        return { data: fallbackData as Testimonial[], error: fallbackError }
      }
    }

    // If still nothing and we are not in PT, try PT
    if (locale !== 'pt') {
      let ptQuery = supabase
        .from('testimonials' as any)
        .select('*')
        .eq('locale', 'pt')
        .order('sort_order', { ascending: true })

      if (sectorId) {
        ptQuery = ptQuery.eq('sector_id', sectorId)
      }

      const { data: ptData, error: ptError } = await ptQuery
      return { data: ptData as Testimonial[], error: ptError }
    }
  }

  return { data: data as Testimonial[], error }
}

export const createTestimonial = async (testimonial: TestimonialInsert) => {
  const { data, error } = await supabase
    .from('testimonials' as any)
    .insert([testimonial])
    .select()
    .single()

  return { data: data as Testimonial, error }
}

export const updateTestimonial = async (
  id: string,
  testimonial: Partial<TestimonialInsert>,
) => {
  const { data, error } = await supabase
    .from('testimonials' as any)
    .update(testimonial)
    .eq('id', id)
    .select()
    .single()

  return { data: data as Testimonial, error }
}

export const deleteTestimonial = async (id: string) => {
  const { error } = await supabase
    .from('testimonials' as any)
    .delete()
    .eq('id', id)

  return { error }
}

// Partner Functions

export const getPartners = async () => {
  const { data, error } = await supabase
    .from('partners' as any)
    .select('*')
    .order('sort_order', { ascending: true })

  return { data: data as Partner[], error }
}

export const createPartner = async (partner: PartnerInsert) => {
  const { data, error } = await supabase
    .from('partners' as any)
    .insert([partner])
    .select()
    .single()

  return { data: data as Partner, error }
}

export const updatePartner = async (
  id: string,
  partner: Partial<PartnerInsert>,
) => {
  const { data, error } = await supabase
    .from('partners' as any)
    .update(partner)
    .eq('id', id)
    .select()
    .single()

  return { data: data as Partner, error }
}

export const deletePartner = async (id: string) => {
  const { error } = await supabase
    .from('partners' as any)
    .delete()
    .eq('id', id)

  return { error }
}

// Case Study Functions

export const getCaseStudiesBySector = async (sectorId: string) => {
  const { data, error } = await supabase
    .from('case_studies' as any)
    .select('*')
    .eq('sector_id', sectorId)
    .order('sort_order', { ascending: true })

  return { data: data as CaseStudy[], error }
}
